import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Superscript from '../../base/Superscript/Superscript.js';
import { ITranslationContextValue } from '../../base/Translate/models/ITranslationContextValue.js';
import { TranslationContext } from '../../base/Translate/Translate.js';

import { IChangeIndicatorConfiguration, IChangeIndicatorProps, IChangeIndicatorState } from './models/index.js';
import Icon from './styledComponents/Icon.js';
import Placeholder from './styledComponents/Placeholder.js';
import ValueDown from './styledComponents/ValueDown.js';
import ValueUp from './styledComponents/ValueUp.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CHANGE_INDICATOR_CATEGORY_KEY, CHANGE_INDICATOR_COMPONENT_KEY } from './ChangeIndicator.meta.js';

export class ChangeIndicator extends ComponentBase<
	IChangeIndicatorProps,
	IChangeIndicatorState,
	IChangeIndicatorConfiguration
> {
	categoryKey = CHANGE_INDICATOR_CATEGORY_KEY;
	componentKey = CHANGE_INDICATOR_COMPONENT_KEY;
	translationContext: ITranslationContextValue | undefined;

	constructor(props: IChangeIndicatorProps) {
		super(props);

		makeObservable(this, {
			iconPosition: computed,
			direction: computed,
			icon: computed,
			value: computed,
		});
	}

	get iconPosition() {
		const { icon } = this.props;
		if (this.direction === 'zero') {
			return 'none';
		}
		const defaultIcon = {
			show: true,
			position: 'right',
		};
		const iconProps = Object.assign(defaultIcon, icon);
		return iconProps.show && iconProps.position;
	}

	get direction(): 'up' | 'down' | 'zero' {
		const { directionValue, value = 0 } = this.props;
		let usedValue = directionValue || value;
		if (typeof usedValue === 'string') {
			usedValue = usedValue
				.replace(',', '')
				.replace(' ', '')
				.replace(String.fromCharCode(160), '')
				.replace(String.fromCharCode(8722), '-');
			usedValue = parseFloat(usedValue);
		}
		return usedValue > 0 ? 'up' : usedValue < 0 ? 'down' : 'zero';
	}

	get icon() {
		const asset =
			this.direction === 'down'
				? 'overview.changeIndicator.changeDownIcon'
				: 'overview.changeIndicator.changeUpIcon';
		let accessibilityText = this.direction === 'down' ? 'Change down' : 'Change up';

		if (this.translationContext) {
			accessibilityText = this.translationContext.translate(accessibilityText);
		}

		return <Icon asset={asset} addAccessibleText={true} accessibilityText={accessibilityText} />;
	}

	get value() {
		const { value = 0, placeholder = 0 } = this.props;
		const direction = this.direction;
		if (direction !== 'zero') {
			const ValueComponent = direction === 'up' ? ValueUp : ValueDown;
			const valueContent = this.getFormattedValue(value);
			return <ValueComponent>{valueContent}</ValueComponent>;
		}
		const placeholderContent = this.getFormattedValue(placeholder, true);
		return <Placeholder>{placeholderContent}</Placeholder>;
	}

	getFormattedValue = (value: any, isPlaceholder?: boolean) => {
		const { format, formatPlaceholder } = this.props;
		return !common.commonUtils.isNullOrEmpty(format) && (!isPlaceholder || formatPlaceholder)
			? common.commonUtils.replacePlaceholders({ values: { value }, text: format })
			: value;
	};

	render() {
		const { className, superscripts } = this.props;
		return (
			<TranslationContext.Consumer>
				{(translationContext) => {
					this.translationContext = translationContext;
					return (
						<Wrapper className={className}>
							{this.iconPosition === 'left' && this.icon}
							{this.value}
							{superscripts && <Superscript>{superscripts.join(',')}</Superscript>}
							{this.iconPosition === 'right' && this.icon}
						</Wrapper>
					);
				}}
			</TranslationContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(
	observer(ChangeIndicator),
	CHANGE_INDICATOR_CATEGORY_KEY,
	CHANGE_INDICATOR_COMPONENT_KEY,
);
